import { useToast } from '@chakra-ui/react';
import { getTenantId } from '@pelicargo/tenants';
import { useHotkeys } from 'react-hotkeys-hook';

import { useQuoteUpdateDefaults } from '../../components/Drawers/QuoteUpdateDrawer';
import { transformToClient } from '../../components/Emails/EmailQuoteForm';
import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { useCurrentRequest } from '../useCurrentRequest';

export const useQuickSendUpdate = <T extends HTMLElement>() => {
  const { defaultContact } = useAuth();
  const { request } = useCurrentRequest();
  const { mutateAsync, isLoading } = trpc.sendQuoteUpdateEmail.useMutation();
  const defaults = useQuoteUpdateDefaults();
  const toast = useToast();

  const quickSendUpdate = async () => {
    if (!defaultContact?.id || request.tenant_id !== getTenantId()) {
      // This is mainly for us
      return toast({
        colorScheme: 'red',
        title: 'Error',
        description: 'Could not determine who you are. Please set up your contact and refresh',
      });
    }
    const transformed = transformToClient(defaults.defaultRequest);
    const toContact = defaults.defaultSelectedContacts?.[0];
    const ccContacts = defaults.defaultSelectedContacts?.slice(1);
    try {
      await mutateAsync({
        requestId: request.id,
        overrides: {
          customMessage: defaults.defaultRequest.customMessage,
          destinationAirportIds: transformed.destinationAirportIds,
          originAirportIds: transformed.originAirportIds,
          preferredWeightUnit: transformed.preferredWeightUnit,
        },
        senderContactId: defaultContact?.id,
        toContact,
        ccContacts,
      });

      toast({
        colorScheme: 'green',
        title: 'Quote Update email sent',
        description: `Recipients: ${defaults.defaultSelectedContacts?.map((x) => x.email).join(',')}`,
      });
    } catch (e) {
      toast({
        colorScheme: 'red',
        title: 'Failed to send email',
        description: e?.message,
      });
    }
  };

  const ref = useHotkeys<T>('mod+enter', quickSendUpdate, { enableOnFormTags: true, preventDefault: true }, [
    quickSendUpdate,
  ]);

  return { quickSendUpdate, isSending: isLoading, ref };
};
